import React, {useState} from 'react';
import {useTranslation} from 'react-i18next';
import MUIDataTable from 'mui-datatables';
import {createTheme, MuiThemeProvider} from '@material-ui/core/styles';
import {Delete} from '@material-ui/icons';
import Spinner from '../Spinner/Spinner';
import {IconButton} from '@material-ui/core';
import './DataTable.css';

function DataTable({
                       customToolbar, customColumns, data,
                       selection, title, onRowsDelete,
                       selectableRows, filter, download,
                       viewColumns, print, loading, loadingDelete, pagination = true,
                       selectedRowIndex = false, setSelectedRowIndex
                   }) {
    const {t} = useTranslation();
    const [currentPage, setCurrentPage] = useState(1);
    const [numberOfRows, setNumberOfRows] = useState(10);

    const CustomToolbarSelect = (selectedRows, displayData, setSelectedRows) => {
        if (data.length > numberOfRows) {
            if (selectedRowIndex && selectedRows?.data?.length === 1) {
                let rowIndex = selectedRows.data[0]?.dataIndex
                setSelectedRowIndex(rowIndex)
            } else if (selectedRows?.data?.length === displayData.length) { // Are all rows selected?
                // starting index found
                let startIndex = numberOfRows * (currentPage - 1)
                let selectedRow = [];
                // rows on the page are found
                for (let i = 0; i < numberOfRows; i++) {
                    // Exceeding the index of the directory is prevented
                    if (displayData.length - 1 < startIndex) {
                        break;
                    }
                    selectedRow.push(startIndex);
                    startIndex++;
                }
                // set new selectedRows
                setSelectedRows(selectedRow);
            }
        }
        return (
            <div>
                {loadingDelete ?
                    <div style={{margin: 14}}>
                        <Spinner color={'#002742'}/>
                    </div> :
                    <IconButton
                        style={{height: 14, marginRight: 6}}
                        onClick={() => {
                            onRowsDelete(selectedRows, displayData, setSelectedRows);
                        }}
                    >
                        <Delete style={{color: '#D75E5E', width: 20, height: 20}}/>
                    </IconButton>
                }
            </div>
        );
    };

    const options = {
        filter: false,
        download: false,
        search: false,
        viewColumns: false,
        print: false,
        sort: true,
        onChangePage: (page) => setCurrentPage(page + 1),
        onChangeRowsPerPage: (rowsPerPage) => setNumberOfRows(rowsPerPage),
        filterType: 'dropdown',
        responsive: 'standard',
        pagination: pagination,
        tableBodyHeight: '100%',
        onRowSelectionChange: selection,
        customToolbar,
        customToolbarSelect: CustomToolbarSelect,
        selectableRows,
        textLabels: {
            body: {
                noMatch: loading ? <Spinner color={'#002742'} style={{width: "100%", height: "100%"}}/> :
                    <div id="no-match-data">{t('CONTENT.DATATABLE.BODY.NOMATCH')}</div>,
                toolTip: 'Sort',
                columnHeaderTooltip: column => `${t('CONTENT.DATATABLE.BODY.COLUMNHEADERTOOLTIP')} ${column.label}`
            },
            pagination: {
                next: t('CONTENT.DATATABLE.PAGINATION.NEXT'),
                previous: t('CONTENT.DATATABLE.PAGINATION.PREVIOUS'),
                rowsPerPage: t('CONTENT.DATATABLE.PAGINATION.ROWSPERPAGE'),
                displayRows: t('CONTENT.DATATABLE.PAGINATION.DISPLAYROWS')
            },
            toolbar: {
                search: t('CONTENT.DATATABLE.TOOLBAR.SEARCH'),
                downloadCsv: t('CONTENT.DATATABLE.TOOLBAR.DOWNLOADCSV'),
                print: t('CONTENT.DATATABLE.TOOLBAR.PRINT'),
                viewColumns: t('CONTENT.DATATABLE.TOOLBAR.VIEWCOLUMNS'),
                filterTable: t('CONTENT.DATATABLE.TOOLBAR.FILTERTABLE')
            },
            filter: {
                all: t('CONTENT.DATATABLE.FILTER.ALL'),
                title: t('CONTENT.DATATABLE.FILTER.TITLE'),
                reset: t('CONTENT.DATATABLE.FILTER.RESET')
            },
            viewColumns: {
                title: t('CONTENT.DATATABLE.VIEWCOLUMNS.TITLE'),
                titleAria: t('CONTENT.DATATABLE.VIEWCOLUMNS.TITLEARIA')
            },
            selectedRows: {
                text: <div style={{fontSize: 11, display: "inline"}}>{t('CONTENT.DATATABLE.SELECTEDROWS.TEXT')}</div>,
                delete: t('CONTENT.DATATABLE.SELECTEDROWS.DELETE'),
                deleteAria: t('CONTENT.DATATABLE.SELECTEDROWS.DELETEARIA')
            }
        }
    };
    const getMuiTheme = () => createTheme({
        overrides: {
            MUIDataTable: {
                paper: {
                    boxShadow: 'none',
                    backgroundColor: 'transparent',
                    position: 'relative',
                    top: 40
                }
            },
            MUIDataTableToolbarSelect: {
                root: {
                    paddingTop: 0,
                    paddingBottom: 0,
                    backgroundColor: 'transparent',
                    position: 'absolute',
                    width: '100%',
                    top: -25
                },
            },
            MUIDataTableBody: {
                emptyTitle: {
                    marginTop: 10,
                    marginBottom: 10
                }
            },
            MUIDataTableToolbar: {
                actions: {
                    display: 'flex',
                    flexDirection: 'row',
                    flex: 'initial'
                }
            },
            MuiTableCell: {
                root: {
                    paddingTop: 0,
                    paddingBottom: 0,
                },
                head: {
                    height: 27
                }
            },
            MUIDataTableHeadCell: {
                sortAction: {
                    '& svg': {}
                }
            },
            MuiToolbar: {
                gutters: {
                    '@media (min-width: 600px)': {
                        paddingLeft: 17
                    }
                },
                regular: {
                    '@media (min-width: 600px)': {
                        minHeight: 'unset'
                    }
                }
            },
            MuiPaper: {
                elevation1: {boxShadow: 'none'}
            }
        }
    });

    return (
        <MuiThemeProvider theme={getMuiTheme()}>
            <div id="datatable-container">
                <MUIDataTable
                    className="datatable-container"
                    data-test="data-table"
                    data={data}
                    columns={customColumns}
                    options={options}
                />
            </div>
        </MuiThemeProvider>
    );

}

export default DataTable;
